import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from 'react-query';
import { 
  CalendarIcon,
  CurrencyDollarIcon,
  TrophyIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  StarIcon,
  BoltIcon,
  ExclamationTriangleIcon,
  ChartBarIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';
import { StarIcon as StarIconSolid } from '@heroicons/react/24/solid';
import api from '../services/api';
import useAuthStore from '../store/authStore';
import ReviewDetailModal from '../components/ReviewDetailModal';

const ContractorStats = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const [period, setPeriod] = useState('all'); // all, month, quarter, year
  const [reviewPage, setReviewPage] = useState(1);
  const [selectedReview, setSelectedReview] = useState(null);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const reviewsPerPage = 3;

  // 통계 데이터 조회
  const { data: stats, isLoading } = useQuery(
    ['contractor-stats', user?.id, period],
    () => api.get('/contractors/my-stats', { params: { period } }).then(res => res.data),
    {
      enabled: user?.user_type === 'contractor'
    }
  );

  // URL 파라미터로 리뷰 팝업 열기
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const reviewId = params.get('review');
    
    if (reviewId && stats?.recentReviews) {
      const review = stats.recentReviews.find(r => r.id === parseInt(reviewId));
      if (review) {
        setSelectedReview(review);
        setShowReviewModal(true);
      }
    }
  }, [location.search, stats]);

  const formatCurrency = (amount) => {
    if (!amount) return '0원';
    const billion = Math.floor(amount / 100000000);
    const million = Math.floor((amount % 100000000) / 10000);
    
    if (billion > 0) {
      return `${billion}억${million > 0 ? ` ${million}만원` : '원'}`;
    } else if (million > 0) {
      return `${million}만원`;
    } else {
      return new Intl.NumberFormat('ko-KR').format(amount) + '원';
    }
  };

  const renderStars = (rating) => {
    return Array.from({ length: 5 }, (_, i) => {
      if (i < Math.floor(rating)) {
        return <StarIconSolid key={i} className="h-5 w-5 text-yellow-400" />;
      } else {
        return <StarIcon key={i} className="h-5 w-5 text-gray-300" />;
      }
    });
  };

  if (user?.user_type !== 'contractor') {
    navigate('/projects');
    return null;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">통계를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  const periodLabels = {
    all: '전체',
    month: '이번 달',
    quarter: '이번 분기',
    year: '올해'
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 헤더 */}
      <div className="bg-gradient-to-b from-blue-600 to-blue-700 text-white pb-32">
        <div className="px-4 pt-6 pb-8">
          <div className="flex items-center justify-between mb-6">
            <h1 className="text-2xl font-bold">내 통계</h1>
            <CalendarIcon className="h-6 w-6 text-blue-200" />
          </div>

          {/* 기간 선택 */}
          <div className="flex gap-2 overflow-x-auto scrollbar-hide">
            {Object.entries(periodLabels).map(([key, label]) => (
              <button
                key={key}
                onClick={() => setPeriod(key)}
                className={`px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-all ${
                  period === key
                    ? 'bg-white text-blue-600'
                    : 'bg-white/20 text-white hover:bg-white/30'
                }`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* 주요 통계 */}
          <div className="mt-8 grid grid-cols-2 gap-4">
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
              <p className="text-blue-100 text-sm mb-1">총 수익</p>
              <p className="text-xl font-bold">
                {formatCurrency(stats?.totalRevenue || 0)}
              </p>
              {stats?.revenueChange !== undefined && (
                <div className={`flex items-center gap-1 mt-1 text-xs ${
                  stats.revenueChange >= 0 ? 'text-green-300' : 'text-red-300'
                }`}>
                  {stats.revenueChange >= 0 ? (
                    <ArrowTrendingUpIcon className="h-3 w-3" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-3 w-3" />
                  )}
                  <span>{Math.abs(stats.revenueChange)}%</span>
                </div>
              )}
            </div>

            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-4">
              <p className="text-blue-100 text-sm mb-1">완료율</p>
              <p className="text-2xl font-bold">{stats?.completionRate || 0}%</p>
              <p className="text-[10px] text-blue-100 mt-1">
                {stats?.completed || 0}/{stats?.total || 0}건
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* 상세 통계 */}
      <div className="px-4 -mt-20">
        {/* 계약 성과 지표 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrophyIcon className="h-5 w-5 text-amber-500" />
            계약 성과 지표
          </h2>

          {/* 계약 성사율 */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl p-5 mb-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-medium text-gray-700">계약 성사율</h3>
              <span className="text-xs text-gray-500">{periodLabels[period]}</span>
            </div>
            
            <div className="flex items-baseline gap-2 mb-3">
              <span className="text-3xl font-bold text-blue-600">
                {stats?.contractRate || 0}%
              </span>
              {stats?.contractRateChange !== undefined && (
                <span className={`text-sm font-medium flex items-center gap-1 ${
                  stats.contractRateChange >= 0 ? 'text-green-600' : 'text-red-600'
                }`}>
                  {stats.contractRateChange >= 0 ? (
                    <ArrowTrendingUpIcon className="h-4 w-4" />
                  ) : (
                    <ArrowTrendingDownIcon className="h-4 w-4" />
                  )}
                  {Math.abs(stats.contractRateChange)}%
                </span>
              )}
            </div>
            
            <div className="grid grid-cols-4 gap-3 text-center">
              <div>
                <p className="text-xl font-bold text-gray-900">{stats?.total || 0}</p>
                <p className="text-xs text-gray-600">전체</p>
              </div>
              <div>
                <p className="text-xl font-bold text-orange-600">{stats?.received || 0}</p>
                <p className="text-xs text-gray-600">접수</p>
              </div>
              <div>
                <p className="text-xl font-bold text-green-600">{stats?.contracted || 0}</p>
                <p className="text-xs text-gray-600">계약</p>
              </div>
              <div>
                <p className="text-xl font-bold text-blue-600">{stats?.completed || 0}</p>
                <p className="text-xs text-gray-600">완료</p>
              </div>
            </div>
          </div>

          {/* 세부 현황 */}
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 bg-blue-500 rounded-full"></span>
                <span className="text-sm font-medium text-gray-700">평균 계약 금액</span>
              </div>
              <span className="text-sm font-bold text-gray-900">
                {formatCurrency(stats?.averageContractAmount || 0)}
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                <span className="text-sm font-medium text-gray-700">완료율</span>
              </div>
              <span className="text-sm font-bold text-gray-900">
                {stats?.completionRate || 0}% ({stats?.completed || 0}/{stats?.contracted || 0})
              </span>
            </div>

            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-xl">
              <div className="flex items-center gap-3">
                <span className="w-2 h-2 bg-red-500 rounded-full"></span>
                <span className="text-sm font-medium text-gray-700">취소율</span>
              </div>
              <span className="text-sm font-bold text-gray-900">
                {stats?.cancelRate || 0}% ({stats?.cancelled || 0}건)
              </span>
            </div>
          </div>
        </div>

        {/* 응답 속도 관리 - 강화된 버전 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BoltIcon className="h-5 w-5 text-orange-500" />
            응답 속도 관리
            <span className="text-xs bg-red-100 text-red-700 px-2 py-1 rounded-full ml-auto">
              목표: 30분 이내
            </span>
          </h2>

          {/* 업계 평균 비교 경고 */}
          <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-xl p-4 mb-4 border border-red-200">
            <div className="flex items-center gap-3">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-8 w-8 text-red-600" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-semibold text-gray-900 mb-2">업계 평균보다 느립니다!</p>
                <div className="flex justify-between items-center mb-1">
                  <span className="text-xs text-gray-600">업계 평균: <span className="font-bold text-green-600">10분</span></span>
                  <span className="text-xs text-gray-600">내 평균: <span className="font-bold text-red-600">{stats?.avgResponseMinutes || 45}분</span></span>
                </div>
                <div className="mt-2 pt-2 border-t border-red-200">
                  <span className="text-xs text-red-600 font-bold">▲ 경쟁사 대비 {(stats?.avgResponseMinutes || 45) - 10}분 늦은 응답으로 고객 이탈 위험</span>
                </div>
              </div>
            </div>
          </div>

          {/* 30분 골든타임 달성률 */}
          <div className="bg-yellow-50 rounded-xl p-4 mb-4">
            <div className="flex items-center justify-between mb-2">
              <p className="text-sm font-medium text-gray-700">30분 골든타임 달성률</p>
              <span className="text-xs text-gray-500">{periodLabels[period]}</span>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="bg-gray-200 rounded-full h-3 overflow-hidden">
                  <div 
                    className={`h-full transition-all duration-500 ${
                      (stats?.responseRate30min || 25) >= 80 ? 'bg-green-500' : 
                      (stats?.responseRate30min || 25) >= 50 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${stats?.responseRate30min || 25}%` }}
                  />
                </div>
              </div>
              <span className="text-xl font-bold text-gray-900">{stats?.responseRate30min || 25}%</span>
            </div>
            <p className="text-xs text-gray-600 mt-2">
              {stats?.responseRate30min < 50 && "⚠️ 절반 이상의 고객이 경쟁사로 이탈 위험"}
            </p>
          </div>

          {/* 시간대별 응답 분포 */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
            <div className="space-y-2">
              <p className="text-xs font-medium text-gray-700">응답 시간 분포</p>
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 w-12">0-10분</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full" style={{ width: '20%' }}></div>
                  </div>
                  <span className="text-xs font-medium w-8 text-right">20%</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 w-12">10-30분</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div className="bg-yellow-500 h-2 rounded-full" style={{ width: '30%' }}></div>
                  </div>
                  <span className="text-xs font-medium w-8 text-right">30%</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-gray-600 w-12">30분+</span>
                  <div className="flex-1 bg-gray-200 rounded-full h-2">
                    <div className="bg-red-500 h-2 rounded-full" style={{ width: '50%' }}></div>
                  </div>
                  <span className="text-xs font-medium text-red-600 w-8 text-right">50%</span>
                </div>
              </div>
            </div>

            <div className="bg-red-50 rounded-lg p-3">
              <p className="text-xs font-medium text-red-800 mb-1">예상 손실</p>
              <p className="text-xl font-bold text-red-600">
                월 {formatCurrency(stats?.estimatedLoss || 15000000)}
              </p>
              <p className="text-xs text-red-600 mt-1">늦은 응답으로 인한 손실</p>
            </div>
          </div>

          {/* 실시간 미응답 현황 */}
          {stats?.pendingDetails && stats.pendingDetails.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-xl p-4">
              <div className="flex items-center justify-between mb-3">
                <p className="text-sm font-medium text-red-800">🚨 즉시 응답 필요</p>
                <span className="text-xs text-red-600">{stats.pendingDetails.length}건</span>
              </div>
              <div className="space-y-2">
                {stats.pendingDetails.slice(0, 3).map((item, index) => (
                  <div key={index} className="flex items-center justify-between text-xs">
                    <span className="text-gray-700">{item.title}</span>
                    <span className="text-red-600 font-medium">{item.elapsed}분 경과</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 평점 및 리뷰 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <StarIcon className="h-5 w-5 text-yellow-500" />
            평점 및 리뷰
          </h2>

          <div className="flex items-center justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-1">
                {renderStars(stats?.averageRating || 0)}
              </div>
              <p className="text-3xl font-bold text-gray-900">
                {stats?.averageRating?.toFixed(1) || '0.0'}
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">총 리뷰</p>
              <p className="text-2xl font-bold text-gray-900">
                {stats?.totalReviews || 0}개
              </p>
            </div>
          </div>

          {/* 최근 리뷰 */}
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="flex items-center justify-between mb-3">
              <p className="text-sm font-medium text-gray-700">최근 리뷰</p>
              {stats?.recentReviews && stats.recentReviews.length > reviewsPerPage && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setReviewPage(prev => Math.max(1, prev - 1))}
                    disabled={reviewPage === 1}
                    className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronLeftIcon className="h-4 w-4" />
                  </button>
                  <span className="text-xs text-gray-500">
                    {reviewPage} / {Math.ceil(stats.recentReviews.length / reviewsPerPage)}
                  </span>
                  <button
                    onClick={() => setReviewPage(prev => 
                      Math.min(Math.ceil(stats.recentReviews.length / reviewsPerPage), prev + 1)
                    )}
                    disabled={reviewPage >= Math.ceil(stats.recentReviews.length / reviewsPerPage)}
                    className="p-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <ChevronRightIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </div>
            
            {stats?.recentReviews && stats.recentReviews.length > 0 ? (
              <div className="space-y-3">
                {stats.recentReviews
                  .slice((reviewPage - 1) * reviewsPerPage, reviewPage * reviewsPerPage)
                  .map((review, index) => (
                  <div 
                    key={review.id || index} 
                    className="bg-gray-50 rounded-lg p-3 cursor-pointer hover:bg-gray-100 transition-colors"
                    onClick={() => {
                      setSelectedReview(review);
                      setShowReviewModal(true);
                    }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-1">
                        {Array.from({ length: 5 }, (_, i) => (
                          <StarIconSolid
                            key={i}
                            className={`h-3 w-3 ${
                              i < review.rating ? 'text-yellow-400' : 'text-gray-300'
                            }`}
                          />
                        ))}
                      </div>
                      <span className="text-xs text-gray-500">
                        {new Date(review.created_at).toLocaleDateString()}
                      </span>
                    </div>
                    <p className="text-xs text-gray-600 mb-1">
                      {review.customer_name} • {review.project_title}
                    </p>
                    {review.comment && (
                      <p className="text-sm text-gray-700 line-clamp-2">
                        "{review.comment}"
                      </p>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-4">
                <p className="text-sm text-gray-500">아직 받은 평가가 없습니다.</p>
                <p className="text-xs text-gray-400 mt-1">고객이 프로젝트 완료 후 평가를 남기면 여기에 표시됩니다.</p>
              </div>
            )}
          </div>
        </div>

        {/* 수익 현황 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-4">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CurrencyDollarIcon className="h-5 w-5 text-green-600" />
            수익 현황
          </h2>

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-green-50 rounded-xl p-4">
              <p className="text-sm text-gray-700 mb-1">이번 달 수익</p>
              <p className="text-xl font-bold text-green-600">
                {formatCurrency(stats?.monthlyRevenue || 0)}
              </p>
            </div>
            <div className="bg-blue-50 rounded-xl p-4">
              <p className="text-sm text-gray-700 mb-1">전체 수익</p>
              <p className="text-xl font-bold text-blue-600">
                {formatCurrency(stats?.totalRevenue || 0)}
              </p>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mt-4">
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm text-gray-600 mb-1">평균 계약금액</p>
              <p className="text-lg font-bold text-gray-900">
                {formatCurrency(stats?.averageContractAmount || 0)}
              </p>
            </div>
            <div className="bg-gray-50 rounded-xl p-4">
              <p className="text-sm text-gray-600 mb-1">최고 계약금액</p>
              <p className="text-lg font-bold text-gray-900">
                {formatCurrency(stats?.maxContractAmount || 0)}
              </p>
            </div>
          </div>

          {/* 월별 추이 */}
          {stats?.monthlyRevenueChart && stats.monthlyRevenueChart.length > 0 && (
            <div className="mt-6">
              <p className="text-sm font-medium text-gray-700 mb-3">월별 수익 추이</p>
              <div className="space-y-2">
                {stats.monthlyRevenueChart.map((data, index) => (
                  <div key={index} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{data.month}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-blue-600 h-2 rounded-full"
                          style={{
                            width: `${(data.revenue / stats.maxMonthlyRevenue) * 100}%`
                          }}
                        />
                      </div>
                      <span className="text-sm font-medium text-gray-900 w-20 text-right">
                        {formatCurrency(data.revenue)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* 종합 성과 대시보드 */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-20">
          <h2 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
            <ChartBarIcon className="h-5 w-5 text-purple-600" />
            종합 성과 대시보드
          </h2>

          {/* 전체 성과 요약 */}
          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="text-center">
              <div className="relative inline-flex items-center justify-center">
                <svg className="w-32 h-32">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="#e5e7eb"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke={stats?.contractRate >= 70 ? '#10b981' : stats?.contractRate >= 40 ? '#f59e0b' : '#ef4444'}
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${(stats?.contractRate || 0) * 3.52} 352`}
                    strokeDashoffset="0"
                    transform="rotate(-90 64 64)"
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute">
                  <p className="text-2xl font-bold">{stats?.contractRate || 0}%</p>
                  <p className="text-xs text-gray-600">계약률</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 mt-2">
                접수 {stats?.received || 0}건 → 계약 {stats?.contracted || 0}건
              </p>
            </div>

            <div className="text-center">
              <div className="relative inline-flex items-center justify-center">
                <svg className="w-32 h-32">
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke="#e5e7eb"
                    strokeWidth="12"
                    fill="none"
                  />
                  <circle
                    cx="64"
                    cy="64"
                    r="56"
                    stroke={stats?.responseRate30min >= 80 ? '#10b981' : stats?.responseRate30min >= 50 ? '#f59e0b' : '#ef4444'}
                    strokeWidth="12"
                    fill="none"
                    strokeDasharray={`${(stats?.responseRate30min || 25) * 3.52} 352`}
                    strokeDashoffset="0"
                    transform="rotate(-90 64 64)"
                    className="transition-all duration-1000"
                  />
                </svg>
                <div className="absolute">
                  <p className="text-2xl font-bold">{stats?.responseRate30min || 25}%</p>
                  <p className="text-xs text-gray-600">30분 내</p>
                </div>
              </div>
              <p className="text-sm text-gray-700 mt-2">
                평균 응답: {stats?.avgResponseMinutes || 45}분
              </p>
            </div>
          </div>

          {/* 월별 추이 차트 */}
          {stats?.monthlyRevenueChart && stats.monthlyRevenueChart.length > 0 && (
            <div className="border-t pt-6">
              <p className="text-sm font-medium text-gray-700 mb-4">월별 수익 및 프로젝트 추이</p>
              <div className="h-48 relative">
                <div className="absolute inset-0 flex items-end justify-between gap-2">
                  {stats.monthlyRevenueChart.map((data, index) => {
                    const height = (data.revenue / stats.maxMonthlyRevenue) * 100;
                    return (
                      <div key={index} className="flex-1 flex flex-col items-center">
                        <div className="w-full relative group">
                          <div 
                            className="w-full bg-gradient-to-t from-blue-600 to-blue-400 rounded-t-lg transition-all duration-500 hover:from-blue-700 hover:to-blue-500"
                            style={{ 
                              height: `${height * 1.5}px`,
                              maxHeight: '150px'
                            }}
                          >
                            <div className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-800 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                              {formatCurrency(data.revenue)}
                            </div>
                          </div>
                        </div>
                        <p className="text-xs text-gray-600 mt-2">{data.month.slice(5)}</p>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* 핵심 지표 미니 차트 */}
          <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t">
            <div className="text-center">
              <div className="flex justify-center mb-2">
                {[1,2,3,4,5].map((i) => (
                  <div 
                    key={i}
                    className={`w-2 h-8 mx-0.5 rounded-full ${
                      i <= Math.round((stats?.completionRate || 0) / 20) 
                        ? 'bg-green-500' 
                        : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-600">완료율</p>
              <p className="text-sm font-bold">{stats?.completionRate || 0}%</p>
            </div>

            <div className="text-center">
              <div className="flex justify-center mb-2">
                {[1,2,3,4,5].map((i) => (
                  <div 
                    key={i}
                    className={`w-2 h-8 mx-0.5 rounded-full ${
                      i <= Math.ceil((stats?.averageRating || 0))
                        ? 'bg-yellow-500' 
                        : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-600">평균 평점</p>
              <p className="text-sm font-bold">{stats?.averageRating?.toFixed(1) || '0.0'}</p>
            </div>

            <div className="text-center">
              <div className="flex justify-center mb-2">
                {[1,2,3,4,5].map((i) => (
                  <div 
                    key={i}
                    className={`w-2 h-8 mx-0.5 rounded-full ${
                      i <= 5 - Math.round((stats?.cancelRate || 0) / 20)
                        ? 'bg-blue-500' 
                        : 'bg-gray-200'
                    }`}
                  />
                ))}
              </div>
              <p className="text-xs text-gray-600">안정성</p>
              <p className="text-sm font-bold">{100 - (stats?.cancelRate || 0)}%</p>
            </div>
          </div>
        </div>
      </div>
      
      {/* 리뷰 상세보기 모달 */}
      <ReviewDetailModal
        review={selectedReview}
        isOpen={showReviewModal}
        onClose={() => {
          setShowReviewModal(false);
          setSelectedReview(null);
          // URL에서 review 파라미터 제거
          const params = new URLSearchParams(location.search);
          params.delete('review');
          const newSearch = params.toString();
          navigate(location.pathname + (newSearch ? `?${newSearch}` : ''), { replace: true });
        }}
      />
    </div>
  );
};

export default ContractorStats;